import React, { useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import {
   getOwnershipClaims,
   grantOwnershipClaim,
   denyOwnershipClaim,
} from '@/api/vendo';

/**
 * Phase 6 — review hardware-ownership claims. When a device of yours is
 * registered on another account, it shows here: Grant (you sold/gave it away)
 * or Deny (it's stolen → stays blocked on the other account).
 */
const OwnershipRequests: React.FC = () => {
   const navigate = useNavigate();
   const queryClient = useQueryClient();
   const [busy, setBusy] = useState<string | null>(null);

   const { data, isLoading } = useQuery({
      queryKey: ['ownership-claims'],
      queryFn: getOwnershipClaims,
      refetchInterval: 30000,
   });
   const claims = data?.claims ?? [];

   const act = async (
      id: string,
      action: 'grant' | 'deny',
      label: string
   ) => {
      if (busy) return;
      setBusy(id + action);
      try {
         if (action === 'grant') await grantOwnershipClaim(id);
         else await denyOwnershipClaim(id);
         queryClient.invalidateQueries({ queryKey: ['ownership-claims'] });
         toast.success(
            action === 'grant'
               ? `Granted — ${label} transferred to the new account`
               : `Denied — ${label} stays blocked on the other account`,
            { autoClose: 2500 }
         );
      } catch (err: any) {
         toast.error(err?.response?.data?.error || `Could not ${action}`);
      } finally {
         setBusy(null);
      }
   };

   return (
      <Wrapper>
         <Header>
            <Title>Ownership requests</Title>
            <BackBtn onClick={() => navigate('/wifi-devices')}>
               ← Back to Devices
            </BackBtn>
         </Header>
         <Subhead>
            Someone registered one of your devices on a different SmartFi
            account. Grant it if you sold or gave it away, or Deny it if it was
            lost or stolen — denied hardware stays locked on the other account.
         </Subhead>

         {isLoading ? (
            <SkeletonLines lines={3} />
         ) : claims.length === 0 ? (
            <Empty>✓ No pending ownership requests.</Empty>
         ) : (
            claims.map((c) => (
               <Card key={c._id}>
                  <CardMain>
                     <Serial>{c.serial || c.hardwareKey}</Serial>
                     <Meta>
                        Claimed by device <b>{c.claimantDeviceId}</b>
                        {c.publicIp && (
                           <>
                              {' · '}last seen {c.publicIp}
                              {c.isp ? ` (${c.isp})` : ''}
                           </>
                        )}
                     </Meta>
                     <Meta $muted>
                        {new Date(c.createdAt).toLocaleString()}
                     </Meta>
                  </CardMain>
                  <Actions>
                     <DenyBtn
                        disabled={!!busy}
                        onClick={() => act(c._id, 'deny', c.serial || 'device')}
                     >
                        {busy === c._id + 'deny' ? 'Denying…' : 'Deny (stolen)'}
                     </DenyBtn>
                     <GrantBtn
                        disabled={!!busy}
                        onClick={() => act(c._id, 'grant', c.serial || 'device')}
                     >
                        {busy === c._id + 'grant' ? 'Granting…' : 'Grant'}
                     </GrantBtn>
                  </Actions>
               </Card>
            ))
         )}
      </Wrapper>
   );
};

export default OwnershipRequests;

const Wrapper = styled.div`
   padding: 20px;
   max-width: 900px;
`;
const Header = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 6px;
`;
const Title = styled.h2`
   margin: 0;
   font-size: 20px;
   color: ${colors.DARK};
`;
const BackBtn = styled.button`
   border: 1px solid #e6e6e6;
   background: #fff;
   border-radius: 8px;
   padding: 7px 12px;
   font-size: 13px;
   cursor: pointer;
`;
const Subhead = styled.p`
   margin: 0 0 16px;
   font-size: 13px;
   line-height: 1.5;
   color: ${colors.GRAY};
   max-width: 640px;
`;
const Empty = styled.div`
   padding: 28px;
   text-align: center;
   color: ${colors.GRAY};
   border: 1px dashed #e0e0e0;
   border-radius: 12px;
`;
const Card = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 16px;
   border: 1px solid #f0a020;
   background: #fff7e6;
   border-radius: 12px;
   padding: 14px 16px;
   margin-bottom: 10px;
`;
const CardMain = styled.div`
   display: flex;
   flex-direction: column;
   gap: 3px;
`;
const Serial = styled.span`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.DARK};
`;
const Meta = styled.span<{ $muted?: boolean }>`
   font-size: 12px;
   color: ${(p) => (p.$muted ? '#9aa0a6' : colors.GRAY)};
`;
const Actions = styled.div`
   display: flex;
   gap: 8px;
   flex-shrink: 0;
`;
const GrantBtn = styled.button`
   border: none;
   border-radius: 8px;
   padding: 8px 16px;
   font-size: 13px;
   font-weight: 600;
   color: #fff;
   background: ${colors.PRIMARY};
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;
const DenyBtn = styled.button`
   border: 1px solid #e57373;
   border-radius: 8px;
   padding: 8px 16px;
   font-size: 13px;
   font-weight: 600;
   color: #c62828;
   background: #fff;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;
